<template>
    <div class="plan-summary-review">
        <section class="plan-summary-review__intro flow__panel">
            <h2>
                {{ myResources.title }}
            </h2>
            <p>{{ myResources.introText }}</p>
        </section>
        <div class="flow__panel flow__container-fluid flow__panel--positioned-buttons">
            <div>
                <section class="grid">
                    <your-property class="plan-summary-review__property-info" :title="myResources.yourPropertyTitle" :property="property" />
                    <hh-accent-panel class="plan-summary-review__options-info">
                        <title-panel class="plan-summary-review__options-info__title-panel plan-summary-review__options-info__title-panel--motivation"
                                     :title="myResources.goalTitle"
                                     :show-change="!scheme.hasFixedMotivation"
                                     @click="$router.push({ name: 'PlanChooser' })">
                            {{ motivationDisplay }}
                        </title-panel>
                        <title-panel class="plan-summary-review__options-info__title-panel plan-summary-review__options-info__title-panel--budget"
                                     :title="myResources.budgetTitle"
                                     :show-change="!scheme.hasFixedBudget"
                                     @click="$router.push({ name: 'PlanChooser' })">
                            Up to {{ currentProject.budgetDisplay }}
                        </title-panel>
                    </hh-accent-panel>
                </section>
                <section>
                    <error-handler class="plan-summary-review__errors" />
                    <hh-accent-panel>
                        <div class="grid cols-3_1 --mobile">
                            <h3>{{ myResources.measuresTitle }}</h3>
                            <div class="--end">
                                <hh-btn :text="commonResources.change" track-operation-state @click="$router.push({ name: 'PlanBuilder' })" />
                            </div>
                        </div>
                        <basket class="plan-summary-review__basket" editable />
                    </hh-accent-panel>
                    <hh-accent-panel class="plan-summary-review__metrics grid">
                        <h3 class="plan-summary-review__metrics__title --not-mobile">
                            {{ myResources.savingsTitle }}
                        </h3>
                        <metrics-display class="plan-summary-review__metrics__display"
                                         mode="review" />
                    </hh-accent-panel>
                    <property-notes class="plan-summary-review__property-notes"
                                    :title="myResources.propertyNotesTitle"
                                    :project="currentProject"
                                    v-if="!scheme.userOnlyTool"
                                    @click="$router.push({ name: 'PlanNotesForm' })">
                        <template v-slot="props">
                            <p v-if="props.notes.length > 0">
                                {{ myResources.aboutPropertyText }}
                            </p>
                            <p v-else>
                                {{ myResources.aboutPropertyTextNoInformation }}
                            </p>
                        </template>
                    </property-notes>
                </section>
            </div>
            <aside class="plan-summary-review__sidebar">
                <hh-accent-panel class="plan-summary-review__aside" show-image>
                    <h3>
                        {{ myResources.sidebarTitle }}
                    </h3>
                    <markdown :content="sidebarText" />
                    <cta-tile class="plan-summary-review__cta"
                              name="plan-summary-review"
                              :button-text="myResources.ctaButton"
                              type="cta"
                              breakout-extended
                              @click="onProceed" />
                </hh-accent-panel>
                <hh-accent-panel v-if="selectedServices.length">
                    <div class="plan-summary-review__title-bar grid cols-3_1 --mobile">
                        <h3>{{ myResources.additionalSupportTitle }}</h3>
                        <div class="--end">
                            <hh-btn :text="commonResources.change" track-operation-state @click="$router.push({ name: 'PlanServices' })" />
                        </div>
                    </div>
                    <p>{{ myResources.additionalSupportText }}</p>
                    <ul>
                        <li v-for="service in selectedServices" :key="service.key">
                            <strong>{{ service.title }}</strong>
                        </li>
                    </ul>
                    <p>{{ myResources.additionalSupportSummary }}</p>
                </hh-accent-panel>
            </aside>
        </div>
    </div>
</template>

<script>

import Vuex from 'vuex'

import strings from '@/helpers/strings'
import ProgressManager from '@/progress-manager'
import Project from '@/models/project'

import AnalyticsPage from './analytics-page'
import MyHomeDataErrorsDisplay from './my-home-data-errors-display'
import InstanceTypeHelper from '@/components/instance-type-helper'
import ResourcesHelper from '@/components/resources-helper'

import Markdown from '@/components/markdown'
import Basket from '@/components/app-core/plan-basket'
import CtaTile from '@/components/app-core/cta-tile'
import ErrorHandler from '@/components/app-core/error-handler'
import MetricsDisplay from '@/components/app-core/metrics-display'

import PropertyNotes from '@/components/plan-summary-review/property-notes'
import TitlePanel from '@/components/plan-summary-review/title-panel'
import YourProperty from '@/components/plan-summary-review/your-property'

export default {
    name: 'PlanSummaryReviewPage',

    mixins: [
        AnalyticsPage,
        InstanceTypeHelper,
        MyHomeDataErrorsDisplay,
        ResourcesHelper,
    ],

    computed: {
        ...Vuex.mapState({

            /**
             * @param {VuexState} stateStore
             * @return {PropertyAsJson|Property}
             */
            property: stateStore => stateStore.property,

            /**
             * @param {VuexState} stateStore
             * @returns {Project}
             */
            currentProject: stateStore => Project.ensure(stateStore.improvements.currentProject),
        }),

        /**
         * Return the resources object for this page.
         * @return {PlanSummaryReviewResources}
         */
        myResources() {
            return this.parseResources('views.planSummaryReview')
        },

        /**
         * @returns {Scheme}
         */
        scheme() {
            return this.$scheme
        },

        /**
         * @returns {string}
         */
        sidebarText() {
            return strings.parse(this.myResources.sidebarText, {
                title: this.servicingScheme ? this.servicingScheme.title : this.scheme.title,
            })
        },

        /**
         * @returns {string}
         */
        motivationDisplay() {
            const motivationResource = this.commonResources.motivations
                .find(mr => mr.key === this.currentProject.motivation)

            return motivationResource ? motivationResource.shortText : ''
        },

        /**
         * @returns {Array<ServiceOffering>}
         */
        selectedServices() {
            const selected = this.currentProject.services.filter(({ value }) => value === 'Y').map(({ key }) => key)

            return this.$scheme.services.filter(service => selected.includes(service.key))
        },
    },

    methods: {
        /**
         * Move to next route.
         */
        onProceed() {
            this.$router.push(ProgressManager.getNext(this.$route.name))
        },
    },

    components: {
        Basket,
        CtaTile,
        ErrorHandler,
        Markdown,
        MetricsDisplay,
        PropertyNotes,
        TitlePanel,
        YourProperty,
    },
}

</script>

<style lang="scss">

.plan-summary-review {
    @include element('intro') {
        p {
            max-width: 55rem;
        }
    }

    @include element('metrics') {
        @include element('title') {
            align-self: center;
        }

        @include element('display') {
            margin: 1rem 0;

            @include bp-min-mid {
                margin: 0;
            }

            .metric:last-of-type {
                border-bottom: 0;
            }
        }
    }

    @include element('aside') {
        margin-top: 1.2rem;

        @include bp-min-wide {
            margin-top: 0;
        }

        .button {
            width: 100%;
        }
    }

    @include element('options-info') {

        @include element('title-panel') {
            margin-bottom: $base-gutter;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    @include element('sidebar') {
        display: flex;
        flex-direction: column-reverse;

        @include bp-min-mid {
            flex-direction: column;
        }
    }

    section:not(.grid) .accent-panel:last-of-type {
        margin-bottom: 0;
    }

    .button {
        min-width: 85px;
    }
}

</style>
